import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import Button from "../components/Common/Button";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import Pagination from "../components/Common/Pagination";
import CurrentUserContext from "../contexts/CurrentUserContext";
import { getCustomerInvoices } from "../lib/api";
import "../components/Common/UnitsListViewComponent.css";

const INVOICES_PAGE_SIZE = 10;

const formatDate = (value) => {
  if (!value) {
    return "Not available";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(parsedDate);
};

const formatCompactDate = (value) => {
  if (!value) {
    return "--";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsedDate);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not available";
  }

  const amount = Number(value);

  if (Number.isNaN(amount)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatCount = (value) => {
  if (value === null || value === undefined) {
    return "Not available";
  }

  return String(value);
};

const formatDurationYears = (months) => {
  if (months === null || months === undefined || months === "") {
    return "--";
  }

  const monthsNumber = Number(months);

  if (Number.isNaN(monthsNumber)) {
    return "--";
  }

  const years = monthsNumber / 12;
  const roundedYears = Math.round(years * 100) / 100;

  return `${roundedYears} ${roundedYears === 1 ? "year" : "years"}`;
};

const isInvoicePastDue = (invoice) => {
  const balance = Number(invoice.amountDue) - Number(invoice.amountPaid);

  if (Number.isNaN(balance) || balance <= 10) {
    return false;
  }

  const dueDate = new Date(invoice.dueDate);

  if (Number.isNaN(dueDate.getTime())) {
    return false;
  }

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  return dueDate.getTime() < startOfToday.getTime();
};

const BillingDetailRow = ({ label, value, className = "" }) => (
  <div className={`rounded-xl bg-[#F8FBFD] px-5 py-5 dark:bg-[#0E2B57] ${className}`}>
    <p className="text-sm font-medium leading-5 tracking-[0.021px] text-theme">
      {label}
    </p>
    <p className="mt-2 text-xl font-semibold leading-7 tracking-[0.021px] text-[#1E2A2C] dark:text-white">
      {value}
    </p>
  </div>
);

export default function Billing() {
  const { currentUser } = useContext(CurrentUserContext);
  const customer = currentUser?.customer;
  const licenseStatusReason = currentUser?.licenseStatus?.reason;
  const isLicenseExpired = licenseStatusReason === "LICENSE_EXPIRED";
  const isUnitLimitExceeded = licenseStatusReason === "UNIT_LIMIT_EXCEEDED";
  const customerUnid = customer?.unid;
  const isOnTrial = customer?.status === "ON_TRIAL";
  const licenseExpiresAt = customer?.licenseExpiresAt;
  const licensedUnits = customer?.numLicensedUnits;
  const totalUnits = customer?.numUnits;

  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [invoicesTotalCount, setInvoicesTotalCount] = useState(0);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [invoicesError, setInvoicesError] = useState("");
  const [invoicesColumnSizing, setInvoicesColumnSizing] = useState(() => {
    try {
      const stored = localStorage.getItem("billingInvoicesColumnWidths");
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      return {};
    }
  });

  const invoicesPageCount = Math.ceil(invoicesTotalCount / INVOICES_PAGE_SIZE);

  const invoiceRows = useMemo(
    () =>
      invoices.map((invoice) => ({
        ...invoice,
        rowId: `${invoice.invoiceId}-${invoice.invoiceDocumentNumber || ""}`,
        formattedDueDate: formatCompactDate(invoice.dueDate),
        formattedExpirationDate: formatCompactDate(invoice.licenseExpiresAt),
        formattedAmountDue: formatCurrency(invoice.amountDue),
        formattedAmountPaid: formatCurrency(invoice.amountPaid),
        formattedDuration: formatDurationYears(invoice.licenseDurationMonths),
        formattedUnits: formatCount(invoice.numUnits),
        isPastDue: isInvoicePastDue(invoice),
      })),
    [invoices]
  );

  const handleInvoicesColumnSizingChange = useCallback((updater) => {
    setInvoicesColumnSizing((previous) => {
      const updated = typeof updater === "function" ? updater(previous) : updater;
      try {
        localStorage.setItem("billingInvoicesColumnWidths", JSON.stringify(updated));
      } catch (error) {
        // ignore write failures
      }
      return updated;
    });
  }, []);

  const invoiceColumns = useMemo(
    () => [
      {
        id: "invoiceDocumentNumber",
        header: "Invoice #",
        size: 180,
        enableResizing: true,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-theme">
              {row.original.invoiceDocumentNumber || "--"}
            </span>
            {row.original.isPastDue && (
              <span className="inline-flex items-center rounded-full bg-[#FDECEC] px-2 py-0.5 text-xs font-semibold text-[#B42318] dark:bg-[#4A1C1C] dark:text-[#FFB4B4]">
                Past Due
              </span>
            )}
          </div>
        ),
      },
      {
        id: "amountDue",
        header: "Amount Due",
        size: 150,
        enableResizing: true,
        cell: ({ row }) => row.original.formattedAmountDue,
      },
      {
        id: "amountPaid",
        header: "Amount Paid",
        size: 150,
        enableResizing: true,
        cell: ({ row }) => row.original.formattedAmountPaid,
      },
      {
        id: "dueDate",
        header: "Due Date",
        size: 170,
        enableResizing: true,
        cell: ({ row }) => row.original.formattedDueDate,
      },
      {
        id: "numUnits",
        header: "Units",
        size: 120,
        enableResizing: true,
        cell: ({ row }) => row.original.formattedUnits,
      },
      {
        id: "duration",
        header: "License Duration",
        size: 160,
        enableResizing: true,
        cell: ({ row }) => row.original.formattedDuration,
      },
      {
        id: "licenseExpiresAt",
        header: "License Expires",
        size: 180,
        enableResizing: true,
        cell: ({ row }) => row.original.formattedExpirationDate,
      },
    ],
    []
  );

  const invoicesGridHeight = useMemo(() => {
    const headerHeight = 50;
    const rowHeight = 64;
    const visibleRows = Math.max(invoiceRows.length, 1);
    const contentHeight = headerHeight + visibleRows * rowHeight;
    const minHeight = headerHeight + rowHeight;
    const maxHeight = 680;
    return Math.max(minHeight, Math.min(maxHeight, contentHeight));
  }, [invoiceRows.length]);

  const invoicesTable = useReactTable({
    data: invoiceRows,
    columns: invoiceColumns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnSizing: invoicesColumnSizing },
    onColumnSizingChange: handleInvoicesColumnSizingChange,
    columnResizeMode: "onChange",
    getRowId: (row) => row.rowId,
  });

  const fetchInvoices = useCallback(async () => {
    if (isOnTrial) {
      return;
    }

    if (!customerUnid) {
      setInvoices([]);
      setInvoicesTotalCount(0);
      setInvoicesError("Customer invoice history is not available yet.");
      return;
    }

    setIsLoadingInvoices(true);
    setInvoicesError("");

    try {
      const response = await getCustomerInvoices({
        pageNumber: invoiceCurrentPage + 1,
        pageSize: INVOICES_PAGE_SIZE,
      });
      const payload = response?.data?.data || {};

      setInvoices(payload.invoices || []);
      setInvoicesTotalCount(payload.count || 0);
    } catch (error) {
      setInvoices([]);
      setInvoicesTotalCount(0);
      setInvoicesError(error.message || "Failed to fetch customer invoices.");
    } finally {
      setIsLoadingInvoices(false);
    }
  }, [customerUnid, invoiceCurrentPage, isOnTrial]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    if (invoiceCurrentPage > 0 && invoicesPageCount > 0 && invoiceCurrentPage > invoicesPageCount - 1) {
      setInvoiceCurrentPage(invoicesPageCount - 1);
    }
  }, [invoiceCurrentPage, invoicesPageCount]);

  return (
    <>
      <div className="titleAndFilters">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between items-start sm:items-center flex-wrap md:flex-nowrap">
          <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]">
            Billing
          </h2>
        </div>
      </div>

      {(isLicenseExpired || isUnitLimitExceeded) && (
        <div
          role="alert"
          className="mt-6 flex items-start gap-3 rounded-2xl border border-[#F3D2D2] bg-[#FFF7F7] px-5 py-5 dark:border-[#5A2630] dark:bg-[#351821]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            className="mt-0.5 shrink-0 text-[#B42318] dark:text-[#FFB4B4]"
            aria-hidden="true"
          >
            <path d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 0 0 3.53 21H20.47A2 2 0 0 0 22.18 18L13.71 3.86A2 2 0 0 0 10.29 3.86Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-[#B42318] dark:text-[#FFB4B4]">
              {isLicenseExpired
                ? isOnTrial
                  ? "Your trial has expired"
                  : "Your license has expired"
                : "License limit exceeded"}
            </p>
            <p className="mt-1 text-sm leading-6 text-[#B42318]/90 dark:text-[#FFB4B4]/90">
              {isLicenseExpired
                ? isOnTrial
                  ? "Your trial expired. Please contact support to continue access."
                  : "Your license expired. Please renew your license to restore access."
                : "You have more units than your license allows. Please contact support to license additional units."}
            </p>
          </div>
        </div>
      )}

      <div className="mt-10 w-full rounded-2xl bg-white shadow-appShadow dark:bg-darkThemeModalBg">
        <div className="rounded-t-2xl bg-[#EAF2F7] px-6 py-6 dark:bg-[#12315F] sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-theme">
            Account Summary
          </p>
          <h3 className="mt-2 text-xl font-semibold leading-7 tracking-[0.021px] text-[#1E2A2C] dark:text-white">
            Billing Details
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-6 tracking-[0.021px] text-dark-grey dark:text-white/75">
            Review your current billing status and license usage information.
          </p>
        </div>
        <div className="px-6 py-6 sm:px-8">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {isOnTrial && (
              <BillingDetailRow label="Account Type" value="Trial Account" />
            )}
            <BillingDetailRow
              label={isOnTrial ? "Trial Expires At" : "License Expires At"}
              value={formatDate(licenseExpiresAt)}
            />
            {!isOnTrial && (
              <BillingDetailRow label="Licensed Units" value={formatCount(licensedUnits)} />
            )}
            <BillingDetailRow label="Total Units" value={formatCount(totalUnits)} />
          </div>
        </div>
      </div>

      {!isOnTrial && (
      <section className="mt-8 w-full rounded-2xl bg-white shadow-appShadow dark:bg-darkThemeModalBg">
        <div className="flex flex-col gap-3 rounded-t-2xl bg-[#F6FAFD] px-6 py-6 dark:bg-[#102B53] sm:px-8 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-theme">
              Invoices
            </p>
            <h3 className="mt-2 text-xl font-semibold leading-7 tracking-[0.021px] text-[#1E2A2C] dark:text-white">
              Invoice History
            </h3>
            <p className="mt-2 max-w-2xl text-sm leading-6 tracking-[0.021px] text-dark-grey dark:text-white/75">
              Review your invoices along with details of each license purchase.
            </p>
          </div>
          <div className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-theme shadow-sm dark:bg-[#0C2343] dark:text-white">
            {invoicesTotalCount} {invoicesTotalCount === 1 ? "invoice" : "invoices"}
          </div>
        </div>

        <div className="px-6 py-6 sm:px-8">
          {invoicesError ? (
            <div className="rounded-2xl border border-[#F3D2D2] bg-[#FFF7F7] px-5 py-5 dark:border-[#5A2630] dark:bg-[#351821]">
              <p className="text-sm font-medium text-[#B42318] dark:text-[#FFB4B4]">
                {invoicesError}
              </p>
              <Button
                className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-theme px-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-theme/85"
                onClick={fetchInvoices}
              >
                Retry
              </Button>
            </div>
          ) : (
            <>
              <div className="unitsListHolder rounded-md scrollVisible overflow-x-auto w-full">
                <div className="units-data-grid" style={{ height: invoicesGridHeight, width: "100%" }}>
                  <table>
                    <thead>
                      {invoicesTable.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} style={{ height: 50 }}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              className="units-table-header-cell"
                              style={{ width: header.getSize() }}
                            >
                              <div className="units-table-header-content text-center sm:text-left w-full">
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext())}
                              </div>
                              {header.column.getCanResize() && (
                                <div
                                  className={`column-resizer ${header.column.getIsResizing() ? "is-resizing" : ""}`}
                                  onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    header.getResizeHandler()(event);
                                  }}
                                  onTouchStart={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    header.getResizeHandler()(event);
                                  }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                />
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {isLoadingInvoices ? (
                        <tr className="units-table-row" style={{ height: 64 }}>
                          <td className="units-table-cell" colSpan={invoiceColumns.length}>
                            <div className="flex items-center justify-center gap-3 text-sm text-dark-grey dark:text-white/80">
                              <LoadingSpinner size="small" />
                              Loading invoices...
                            </div>
                          </td>
                        </tr>
                      ) : invoiceRows.length > 0 ? (
                        invoicesTable.getRowModel().rows.map((row) => (
                          <tr key={row.id} className="units-table-row" style={{ height: 64 }}>
                            {row.getVisibleCells().map((cell) => (
                              <td
                                key={cell.id}
                                className="units-table-cell"
                                style={{ width: cell.column.getSize() }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr className="units-table-row" style={{ height: 64 }}>
                          <td className="units-table-cell" colSpan={invoiceColumns.length}>
                            <div className="text-center text-sm text-dark-grey dark:text-white/80">
                              No invoices
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {invoicesPageCount > 1 && (
                <Pagination
                  pageCount={invoicesPageCount}
                  currentPage={invoiceCurrentPage}
                  onPageChange={({ selected }) => setInvoiceCurrentPage(selected)}
                />
              )}
            </>
          )}
        </div>
      </section>
      )}
    </>
  );
}
